import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { dealerAPI } from './dealerAPI';

// Async thunks
export const fetchDealers = createAsyncThunk(
  'dealers/fetchDealers',
  async (_, { dispatch }) => {
    const dealers = [];
    await dealerAPI.fetchDealers((dealer) => {
      dealers.push(dealer);
      dispatch(addDealer(dealer));
    });
    return dealers;
  }
);

export const addDealerAsync = createAsyncThunk(
  'dealers/addDealer',
  async (dealerData, { dispatch, rejectWithValue }) => {
    try {
      const response = await dealerAPI.addDealer(dealerData);
      console.log("Add dealer response:", response);
      dispatch(setSuccessMessage('Dealer added successfully!'));
      // Don't clear and refetch immediately - let the success message handle it
      return response;
    } catch (error) {
      console.error("Add dealer error:", error);
      dispatch(setErrorMessage(error.message || 'Failed to add dealer'));
      return rejectWithValue(error.message);
    }
  }
);

export const editDealerAsync = createAsyncThunk(
  'dealers/editDealer',
  async ({ dealerId, dealerData }, { dispatch, rejectWithValue }) => {
    try {
      const response = await dealerAPI.editDealer(dealerId, dealerData);
      console.log("Edit dealer response:", response);
      dispatch(setSuccessMessage('Dealer updated successfully!'));
      return response;
    } catch (error) {
      console.error("Edit dealer error:", error);
      dispatch(setErrorMessage(error.message || 'Failed to update dealer'));
      return rejectWithValue(error.message);
    }
  }
);

export const deleteDealerAsync = createAsyncThunk(
  'dealers/deleteDealer',
  async (dealerId, { dispatch, rejectWithValue }) => {
    try {
      const response = await dealerAPI.deleteDealer(dealerId);
      console.log("Delete dealer response:", response);
      dispatch(setSuccessMessage('Dealer deleted successfully!'));
      return response;
    } catch (error) {
      console.error("Delete dealer error:", error);
      dispatch(setErrorMessage(error.message || 'Failed to delete dealer'));
      return rejectWithValue(error.message);
    }
  }
);

export const addOfficeAsync = createAsyncThunk(
  'dealers/addOffice',
  async (officeData, { dispatch, rejectWithValue }) => {
    try {
      const response = await dealerAPI.addOffice(officeData);
      console.log("Add office response:", response);
      dispatch(setSuccessMessage('Office added successfully!'));
      return response;
    } catch (error) {
      console.error("Add office error:", error);
      dispatch(setErrorMessage(error.message || 'Failed to add office'));
      return rejectWithValue(error.message);
    }
  }
);

const dealerSlice = createSlice({
  name: 'dealers',
  initialState: {
    dealers: [],
    loading: false,
    error: null,
    successMessage: null,
    totalRecords: 0,
    refreshData: false,
  },
  reducers: {
    addDealer: (state, action) => {
      const exists = state.dealers.some(d => d.dealerId === action.payload.dealerId);
      if (!exists && action.payload.dealerId) {
        state.dealers.push(action.payload);
        state.totalRecords = state.dealers.length;
      }
    },
    clearDealers: (state) => {
      state.dealers = [];
      state.totalRecords = 0;
    },
    setSuccessMessage: (state, action) => {
      state.successMessage = action.payload;
    },
    setErrorMessage: (state, action) => {
      state.error = action.payload;
    },
    clearMessages: (state) => {
      state.successMessage = null;
      state.error = null;
    },
    setRefreshData: (state, action) => {
      state.refreshData = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDealers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDealers.fulfilled, (state, action) => {
        state.loading = false;
        // A fetch is the source of truth. Replacing the list ensures edited
        // records are not left stale by the streaming de-duplication logic.
        state.dealers = action.payload;
        state.totalRecords = action.payload.length;
      })
      .addCase(fetchDealers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch dealers';
      })
      .addCase(addDealerAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(addDealerAsync.fulfilled, (state) => {
        state.loading = false;
        state.refreshData = true;
      })
      .addCase(addDealerAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(editDealerAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(editDealerAsync.fulfilled, (state) => {
        state.loading = false;
        state.refreshData = true;
      })
      .addCase(editDealerAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteDealerAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteDealerAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.dealers = state.dealers.filter(
          dealer => String(dealer.dealerId) !== String(action.meta.arg)
        );
        state.totalRecords = state.dealers.length;
        state.refreshData = true;
      })
      .addCase(deleteDealerAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(addOfficeAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(addOfficeAsync.fulfilled, (state) => {
        state.loading = false;
        state.refreshData = true;
      })
      .addCase(addOfficeAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { 
  addDealer, 
  clearDealers, 
  setSuccessMessage, 
  setErrorMessage, 
  clearMessages,
  setRefreshData 
} = dealerSlice.actions;

export const selectAllDealers = (state) => state.dealers.dealers;
export const selectDealersLoading = (state) => state.dealers.loading;
export const selectDealersError = (state) => state.dealers.error;
export const selectSuccessMessage = (state) => state.dealers.successMessage;
export const selectRefreshData = (state) => state.dealers.refreshData;

export default dealerSlice.reducer;
